import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Menu, X, Search } from "lucide-react";
import API from "../../../util/API";
import { useNavigate, useLocation } from "react-router-dom";
import { buildCategoryHref, slugifyCategory } from "../../../util/categoryUrl";

// Enterprise Category Header (Search / Sort / Filter REMOVED)
// - Clean category navigation only
// - Desktop mega-menu + mobile category drawer
// - Brand accent preserved with warm cherry tokens

/* ------------------------------
   Category data is loaded from backend
   GET /api/category
------------------------------ */

const slugify = slugifyCategory;

const cx = (...a) => a.filter(Boolean).join(" ");

const safeStr = (v) => String(v ?? "");
const normalize = (v) => safeStr(v).toLowerCase().trim();
const CORPORATE_FONT_FAMILY = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

const filterCategoryTree = (cat, needle) => {
  const n = normalize(needle);
  if (!n) return cat;
  const self = normalize(`${safeStr(cat?.name)} ${safeStr(cat?.description)}`);
  const kids = Array.isArray(cat?.subcategories) ? cat.subcategories : [];
  const filteredKids = kids.map((c) => filterCategoryTree(c, n)).filter(Boolean);
  if (self.includes(n) || filteredKids.length) {
    return { ...cat, subcategories: filteredKids };
  }
  return null;
};

/* ------------------------------
   Desktop Category Navigation
------------------------------ */
const CategoryNavigationBar = memo(function CategoryNavigationBar({ categoryData, showSuperDeals = true }) {
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [focusedSubKey, setFocusedSubKey] = useState(null);
  const [dropdownLeft, setDropdownLeft] = useState(0);
  const [dropdownWidth, setDropdownWidth] = useState(520);
  const navRef = useRef(null);
  const megaScrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const currentCategoryId = useMemo(() => {
    if (!String(location.pathname || "").startsWith("/category/")) return null;
    try {
      return new URLSearchParams(location.search).get("cid");
    } catch {
      return null;
    }
  }, [location.pathname, location.search]);

  const activeCategory = useMemo(
    () => categoryData.find((c) => c._id === hoveredId) || null,
    [categoryData, hoveredId]
  );

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const forwardWheelToPage = (e) => {
    if (!e) return;
    if (e.shiftKey) return;

    const dy = Number(e.deltaY || 0);
    const dx = Number(e.deltaX || 0);
    if (!dy) return;
    if (Math.abs(dy) <= Math.abs(dx)) return;

    e.preventDefault();
    window.scrollBy({ top: dy, left: 0, behavior: "auto" });
  };

  const handleDesktopNavWheel = (e) => {
    const menu = megaScrollRef.current;
    if (menu && menu.contains(e.target)) {
      const canScroll = menu.scrollHeight > menu.clientHeight + 1;
      if (!canScroll) {
        forwardWheelToPage(e);
        return;
      }

      const dy = Number(e.deltaY || 0);
      const dx = Number(e.deltaX || 0);
      if (!dy) return;
      if (e.shiftKey) return;
      if (Math.abs(dy) <= Math.abs(dx)) return;

      const maxTop = Math.max(0, menu.scrollHeight - menu.clientHeight);
      const atTop = menu.scrollTop <= 0;
      const atBottom = menu.scrollTop >= maxTop - 1;

      // If the menu is already at an edge, scroll the page instead of "locking".
      if ((dy < 0 && atTop) || (dy > 0 && atBottom)) {
        forwardWheelToPage(e);
      }
      return;
    }

    forwardWheelToPage(e);
  };

  const handleEnter = (category, e) => {
    setHoveredId(category._id);
    if (!navRef.current) return;

    const linkRect = e.currentTarget.getBoundingClientRect();
    const navRect = navRef.current.getBoundingClientRect();

    const center = linkRect.left - navRect.left + linkRect.width / 2;

    // Compact mega menu width and clamp to container to prevent overflow.
    const w = clamp(Math.min(720, navRect.width - 24), 360, 720);
    const leftEdge = clamp(center - w / 2, 12, navRect.width - w - 12);

    setDropdownWidth(w);
    setDropdownLeft(Math.round(leftEdge));
  };

  const goToCategory = (category, categoryPath = [], labelOverride) => {
    const label = String(labelOverride || category?.name || categoryPath[categoryPath.length - 1] || "").trim();
    if (!label) return;
    navigate(buildCategoryHref({ categoryId: category?._id, name: label, categoryPath }));
  };

  const nodeKey = (node, fallback) => String(node?._id || node?.name || fallback);

  const topLevelSubs = useMemo(
    () => (Array.isArray(activeCategory?.subcategories) ? activeCategory.subcategories : []),
    [activeCategory]
  );

  useEffect(() => {
    if (!topLevelSubs.length) {
      setFocusedSubKey(null);
      return;
    }
    const hasCurrent = topLevelSubs.some((node, idx) => nodeKey(node, `sub-${idx}`) === focusedSubKey);
    if (!hasCurrent) {
      setFocusedSubKey(nodeKey(topLevelSubs[0], "sub-0"));
    }
  }, [topLevelSubs, focusedSubKey]);

  const focusedSub = useMemo(() => {
    if (!topLevelSubs.length) return null;
    return topLevelSubs.find((node, idx) => nodeKey(node, `sub-${idx}`) === focusedSubKey) || topLevelSubs[0];
  }, [topLevelSubs, focusedSubKey]);

  const focusedSubChildren = useMemo(() => {
    const children = Array.isArray(focusedSub?.subcategories) ? focusedSub.subcategories : [];
    if (children.length) return children;
    return focusedSub ? [focusedSub] : [];
  }, [focusedSub]);

  return (
    <div
      ref={navRef}
      className="relative hidden border-b border-slate-200/70 bg-white shadow-none lg:flex"
      onMouseLeave={() => setHoveredId(null)}
      onWheelCapture={handleDesktopNavWheel}
      style={{ fontFamily: CORPORATE_FONT_FAMILY }}
    >
      <div className="w-full">
        <div className="mx-auto flex h-10 w-full max-w-[1680px] items-center gap-3 overflow-x-auto px-4 sm:px-5 lg:px-7 xl:px-12 2xl:px-[68px]">
          <button
            type="button"
            onClick={() => navigate("/categories")}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 border-r border-slate-200 pr-3 text-sm font-normal text-[#20121A] shadow-none transition-colors hover:text-[#B5163A]"
          >
            <Menu size={17} strokeWidth={2.4} aria-hidden="true" />
            All Categories
          </button>
          {showSuperDeals ? (
            <button
              type="button"
              onClick={() => navigate("/campaign/super-deals")}
              className="shrink-0 text-sm font-normal text-[#D2042D] transition-colors hover:text-[#B5163A]"
            >
              SuperDeals
            </button>
          ) : null}
          {categoryData.map((cat) => (
            <a
              key={cat._id}
              href={`#${slugify(cat.name)}`}
              onMouseEnter={(e) => handleEnter(cat, e)}
              onFocus={(e) => handleEnter(cat, e)}
              onClick={(e) => {
                e.preventDefault();
                setSelectedId(cat._id);
                goToCategory(cat);
              }}
              aria-current={
                currentCategoryId && currentCategoryId === String(cat._id) ? "page" : undefined
              }
              className={
                "group relative inline-flex h-8 shrink-0 items-center gap-1 border-b border-transparent px-1 text-[13px] font-normal transition-colors " +
                ((hoveredId === cat._id ||
                  selectedId === cat._id ||
                  (currentCategoryId && currentCategoryId === String(cat._id)))
                  ? "border-[#B5163A] text-[#B5163A]"
                  : "text-[#59424D] hover:text-[#B5163A]")
              }
            >
              {cat.name}
              {cat.subcategories?.length > 0 && (
                <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
              )}
            </a>
          ))}
        </div>
      </div>

      {activeCategory && activeCategory.subcategories?.length > 0 && (
        <div
          className="absolute top-full z-40"
          style={{ left: dropdownLeft, width: dropdownWidth }}
        >
          <div className="mt-1.5 overflow-hidden rounded-xl border border-slate-100 bg-white shadow-none">
            <div className="grid min-h-[220px] grid-cols-12">
              <div className="col-span-4 border-r border-slate-100 p-1.5">
                <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  Browse
                </p>
                <nav className="mt-0.5 space-y-px">
                  {topLevelSubs.map((sub, idx) => {
                    const key = nodeKey(sub, `sub-${idx}`);
                    const isActive = focusedSub ? key === nodeKey(focusedSub, "focused-sub") : idx === 0;
                    return (
                      <button
                        key={key}
                        type="button"
                        onMouseEnter={() => setFocusedSubKey(key)}
                        onFocus={() => setFocusedSubKey(key)}
                        onClick={() => goToCategory(activeCategory, [sub?.name], sub?.name)}
                        className={
                          "group flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors " +
                          (isActive
                            ? "font-medium text-slate-900"
                            : "font-normal text-slate-500 hover:text-slate-900")
                        }
                      >
                        <span className="truncate">{sub?.name}</span>
                        <ChevronRight
                          className={
                            "h-3 w-3 shrink-0 transition-colors " +
                            (isActive ? "text-slate-400" : "text-slate-300 group-hover:text-slate-400")
                          }
                          aria-hidden="true"
                        />
                      </button>
                    );
                  })}
                </nav>
              </div>

              <div className="col-span-8 p-2.5" ref={megaScrollRef}>
                {focusedSub ? (
                  <>
                    <div className="flex items-center justify-between gap-3 px-0.5 pb-1.5">
                      <div className="min-w-0">
                        <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                          {activeCategory.name}
                        </p>
                        <p className="truncate text-[13px] font-semibold text-slate-900">{focusedSub?.name}</p>
                      </div>
                      <a
                        href={`#${slugify(focusedSub?.name)}`}
                        onClick={(e) => {
                          e.preventDefault();
                          goToCategory(activeCategory, [focusedSub?.name], focusedSub?.name);
                        }}
                        className="shrink-0 text-xs font-medium text-slate-600 underline underline-offset-2 transition-colors hover:text-slate-900"
                      >
                        View all
                      </a>
                    </div>

                    {focusedSubChildren.length ? (
                      <div
                        className={
                          "mt-2 grid gap-1.5 " + (focusedSubChildren.length > 6 ? "grid-cols-2" : "grid-cols-1")
                        }
                      >
                        {focusedSubChildren.map((item, idx) => {
                          const key = nodeKey(item, `child-${idx}`);
                          const deepChildren = Array.isArray(item?.subcategories) ? item.subcategories : [];
                          return (
                            <div
                              key={key}
                              className="rounded-lg border border-slate-100 bg-white p-2.5 transition-colors hover:border-slate-200"
                            >
                              <a
                                href={`#${slugify(item?.name)}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  const itemPath =
                                    item === focusedSub ? [focusedSub?.name] : [focusedSub?.name, item?.name];
                                  goToCategory(activeCategory, itemPath, item?.name);
                                }}
                                className="block truncate text-[13px] font-medium text-slate-900 transition-colors hover:text-black"
                              >
                                {item?.name}
                              </a>

                              {deepChildren.length ? (
                                <div className="mt-1.5 flex flex-wrap gap-1">
                                  {deepChildren.slice(0, 6).map((leaf, leafIdx) => (
                                    <a
                                      key={nodeKey(leaf, `leaf-${leafIdx}`)}
                                      href={`#${slugify(leaf?.name)}`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        goToCategory(activeCategory, [focusedSub?.name, item?.name, leaf?.name], leaf?.name);
                                      }}
                                      className="inline-flex items-center rounded-full border border-slate-100 bg-white px-2 py-0.5 text-[11px] font-normal text-slate-600 transition-colors hover:border-slate-200 hover:text-slate-900"
                                    >
                                      {leaf?.name}
                                    </a>
                                  ))}
                                  {deepChildren.length > 6 ? (
                                    <span className="inline-flex items-center rounded-full border border-slate-100 bg-white px-2 py-0.5 text-[11px] font-normal text-slate-500">
                                      +{deepChildren.length - 6} more
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="mt-2 rounded-lg border border-slate-100 bg-white p-2.5 text-[13px] text-slate-500">
                        No subcategories available.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-lg border border-slate-100 bg-white p-2.5 text-[13px] text-slate-500">
                    No categories available.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

/* ------------------------------
   Mobile Category Menu
------------------------------ */
const MobileNavMenu = memo(function MobileNavMenu({ isOpen, onClose, categoryData }) {
  const [query, setQuery] = useState("");
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    // reset state each open so it feels clean
    setQuery("");
    setOpenKeys(new Set());
  }, [isOpen]);

  const filtered = useMemo(() => {
    const needle = query.trim();
    if (!needle) return categoryData;
    return (Array.isArray(categoryData) ? categoryData : [])
      .map((c) => filterCategoryTree(c, needle))
      .filter(Boolean);
  }, [categoryData, query]);

  const toggle = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const Item = ({ category, level = 0, pathKey, rootCategory, categoryPath = [] }) => {
    const kids = Array.isArray(category?.subcategories) ? category.subcategories : [];
    const hasSub = kids.length > 0;
    const isSearching = Boolean(query.trim());
    const isExpanded = isSearching ? hasSub : openKeys.has(pathKey);
    const root = rootCategory || category;
    const nextPath = root === category ? [] : [...categoryPath, category?.name];

    // Keep indent subtle and readable on mobile (avoid huge left padding)
            const pad = 12 + level * 10; // px

    return (
      <div className="select-none">
        <button
          type="button"
          onClick={() => {
            if (hasSub && !isSearching) {
              toggle(pathKey);
              return;
            }
            const label = String(category?.name || "").trim();
            if (label) {
              navigate(buildCategoryHref({ categoryId: root?._id, name: label, categoryPath: nextPath }));
            }
            onClose();
          }}
          className={cx(
            "group w-full flex items-center justify-between gap-3 border-b border-slate-100 px-2 py-2 text-left transition-colors",
            isExpanded ? "text-[#B5163A]" : "text-[#20121A] hover:text-[#B5163A]"
          )}
          style={{ paddingLeft: `${pad}px` }}
        >
          <span
            className={cx(
              "min-w-0 text-sm font-normal truncate"
            )}
            title={category?.name}
          >
            {category?.name}
          </span>

          {hasSub ? (
            <ChevronDown
              className={cx(
                "w-4 h-4 shrink-0 transition-transform",
                isExpanded ? "rotate-180 text-[#B5163A]" : "text-[#7D6872] group-hover:text-[#B5163A]"
              )}
            />
          ) : (
            <span className="w-4 h-4 shrink-0" />
          )}
        </button>

        {isExpanded && hasSub ? (
          <div className="mt-1">
            {kids.map((c, idx) => (
              <Item
                key={c?._id || `${pathKey}/${idx}`}
                category={c}
                level={level + 1}
                pathKey={`${pathKey}/${idx}`}
                rootCategory={root}
                categoryPath={nextPath}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-[1000] lg:hidden ${isOpen ? '' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
        style={{ visibility: isOpen ? "visible" : "hidden" }}
      />
      <div 
        className={`fixed top-0 left-0 h-full w-[84vw] max-w-[320px] bg-white shadow-none z-[1001] transform transition-[transform,visibility] duration-300 lg:hidden flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
        aria-hidden={!isOpen}
        style={{ visibility: isOpen ? "visible" : "hidden" }}
      >
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <div className="min-w-0">
            <h3 className="text-base font-medium text-slate-900">Categories</h3>
            <p className="text-xs text-slate-500">Browse by category (multi-level)</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center border border-slate-200 bg-white text-slate-700 transition-colors hover:bg-slate-50 shadow-none"
            aria-label="Close categories"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search categories..."
              className="h-10 w-full border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 shadow-none"
            />
          </div>
        </div>

        <nav className="p-2 flex-1 overflow-y-auto">
          {filtered.length ? (
            filtered.map((cat, idx) => (
              <Item key={cat?._id || idx} category={cat} pathKey={String(idx)} rootCategory={cat} />
            ))
          ) : (
            <div className="p-4 text-sm text-slate-600">
              No categories match "{query.trim()}".
            </div>
          )}
        </nav>
      </div>
    </>
  );
});

/* ------------------------------
   Main Header Wrapper
------------------------------ */
export default function CategoryHeaderWrapper({
  mobileOpen: mobileOpenProp,
  setMobileOpen: setMobileOpenProp,
  showMobileHeader = true,
} = {}) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [mobileOpenInternal, setMobileOpenInternal] = useState(false);
  const [showDesktopCategories, setShowDesktopCategories] = useState(true);
  const [navReloadKey, setNavReloadKey] = useState(0);
  const [campaignSlugs, setCampaignSlugs] = useState(null);
  const lastScrollYRef = useRef(0);
  const scrollAccumRef = useRef(0);
  const lastDirRef = useRef(0);
  const visibilityRef = useRef({ categories: true, lastToggleAt: 0 });

  const mobileOpen = typeof mobileOpenProp === "boolean" ? mobileOpenProp : mobileOpenInternal;
  const setMobileOpen = typeof setMobileOpenProp === "function" ? setMobileOpenProp : setMobileOpenInternal;

  useEffect(() => {
    let cancelled = false;
    API.get("/settings/campaigns")
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data?.campaigns) ? res.data.campaigns : [];
        setCampaignSlugs(list.map((c) => String(c?.slug || c?.id || "").trim().toLowerCase()));
      })
      .catch(() => {
        if (cancelled) return;
        setCampaignSlugs(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const showSuperDeals = campaignSlugs === null || campaignSlugs.includes("super-deals");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await API.get("/category");
        const list = Array.isArray(res.data) ? res.data : res.data?.data;
        setCategories(Array.isArray(list) ? list : []);
      } catch (err) {
        setCategories([]);
        setLoadError("categories");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navReloadKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const TOP_THRESHOLD = 12;
    const MIN_DELTA = 6;
    const ACCUM_TRIGGER = 20;
    const TOGGLE_COOLDOWN_MS = 180;
    let ticking = false;

    lastScrollYRef.current = window.scrollY || 0;
    scrollAccumRef.current = 0;
    lastDirRef.current = 0;
    visibilityRef.current = { categories: true, lastToggleAt: 0 };

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        const lastY = lastScrollYRef.current;
        const delta = currentY - lastY;
        const absDelta = Math.abs(delta);
        const dir = delta > 0 ? 1 : delta < 0 ? -1 : 0;
        const now = Date.now();

        if (currentY <= TOP_THRESHOLD) {
          if (!visibilityRef.current.categories) {
            setShowDesktopCategories(true);
            visibilityRef.current.categories = true;
            visibilityRef.current.lastToggleAt = now;
          }
          scrollAccumRef.current = 0;
          lastDirRef.current = 0;
        } else if (absDelta >= MIN_DELTA && dir !== 0) {
          if (dir !== lastDirRef.current) {
            scrollAccumRef.current = 0;
            lastDirRef.current = dir;
          }

          scrollAccumRef.current += absDelta;

          if (scrollAccumRef.current >= ACCUM_TRIGGER) {
            if (now - visibilityRef.current.lastToggleAt < TOGGLE_COOLDOWN_MS) {
              scrollAccumRef.current = 0;
              lastScrollYRef.current = currentY;
              ticking = false;
              return;
            }

            if (dir > 0) {
              if (visibilityRef.current.categories) {
                setShowDesktopCategories(false);
                visibilityRef.current.categories = false;
                visibilityRef.current.lastToggleAt = now;
              }
            } else {
              if (!visibilityRef.current.categories) {
                setShowDesktopCategories(true);
                visibilityRef.current.categories = true;
                visibilityRef.current.lastToggleAt = now;
              }
            }
            scrollAccumRef.current = 0;
          }
        }

        lastScrollYRef.current = currentY;
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const desktopCategoryClass = showDesktopCategories
    ? "lg:max-h-24 lg:opacity-100 lg:translate-y-0 lg:overflow-visible"
    : "lg:max-h-0 lg:opacity-0 lg:-translate-y-2 lg:pointer-events-none lg:overflow-hidden";

  return (
    <>
      <header className="relative z-30" style={{ fontFamily: CORPORATE_FONT_FAMILY }}>
        {showMobileHeader && (
          <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="p-1.5 border border-slate-200 bg-white text-slate-700 shadow-none"
              aria-label="Open categories"
            >
              <Menu size={18} />
            </button>
            <span className="text-sm font-medium text-slate-900">Categories</span>
          </div>
        )}

        <div
          className={`overflow-hidden transition-[max-height,opacity,transform] duration-300 ease-out ${desktopCategoryClass}`}
        >
          {loading ? (
            <div className="h-11 flex items-center justify-center text-sm text-slate-500 bg-white border-b">
              <span role="status" aria-live="polite">Loading categories…</span>
            </div>
          ) : !categories.length && loadError ? (
            <div className="h-11 flex items-center justify-center gap-2 text-sm bg-white border-b">
              <span className="text-slate-600">Couldn’t load categories.</span>
              <button
                type="button"
                onClick={() => setNavReloadKey((k) => k + 1)}
                className="font-semibold text-[#B5163A] hover:underline underline-offset-2"
              >
                Retry
              </button>
            </div>
          ) : (
            <CategoryNavigationBar categoryData={categories} showSuperDeals={showSuperDeals} />
          )}
        </div>
      </header>

      <MobileNavMenu
        isOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        categoryData={categories}
      />
    </>
  );
}
